import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DateCell } from '../components/DateCell';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { ReviewFormModal } from '../features/managementReview/ReviewFormModal';
import {
  createManagementReview,
  fetchManagementReviews,
} from '../features/managementReview/managementReviewApi';
import type { ManagementReviewSummary, MgmtReviewStatus } from '../features/managementReview/types';
import {
  REVIEW_STATUS_LABEL,
  formatPeriodLabel,
  reviewStatusBadgeClass,
} from '../features/managementReview/managementReviewUtils';

const STATUSES = Object.keys(REVIEW_STATUS_LABEL) as MgmtReviewStatus[];

export function ManagementReviewPage() {
  const { canEdit } = usePlatformAccessLevel('management_review.access');
  const [items, setItems] = useState<ManagementReviewSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchManagementReviews({
        page,
        pageSize: 20,
        status: filterStatus || undefined,
        search: search.trim() || undefined,
      });
      setItems(result.data);
      setTotalPages(result.pagination.pages);
    } catch {
      setError('No se pudieron cargar las revisiones por la dirección.');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async (payload: Parameters<typeof createManagementReview>[0]) => {
    await createManagementReview(payload);
    await load();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Revisión por la dirección</h1>
          <p className="text-muted-foreground text-sm">
            Actas con entradas agregadas y decisiones (ISO 9.3)
          </p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary" onClick={() => setModalOpen(true)}>
            Nueva revisión
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <select
          className="input w-full"
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
          aria-label="Filtrar por estado"
        >
          <option value="">Todos los estados</option>
          {STATUSES.map((status) => (
            <option key={status} value={status}>
              {REVIEW_STATUS_LABEL[status]}
            </option>
          ))}
        </select>
        <input
          type="search"
          className="input w-full"
          placeholder="Buscar por título…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          aria-label="Buscar por título"
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3">Título</th>
              <th className="text-left p-3">Período</th>
              <th className="text-left p-3">Fecha revisión</th>
              <th className="text-left p-3">Estado</th>
              <th className="text-left p-3">Participantes</th>
              <th className="text-left p-3">Decisiones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-muted-foreground">
                  Sin revisiones registradas
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t border-border hover:bg-muted/30">
                  <td className="p-3">
                    <Link to={`/revision-direccion/${item.id}`} className="font-medium hover:underline">
                      {item.title}
                    </Link>
                  </td>
                  <td className="p-3">{formatPeriodLabel(item.periodStart, item.periodEnd)}</td>
                  <td className="p-3">
                    <DateCell value={item.reviewDate} />
                  </td>
                  <td className="p-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${reviewStatusBadgeClass(item.status)}`}
                    >
                      {REVIEW_STATUS_LABEL[item.status]}
                    </span>
                  </td>
                  <td className="p-3">{item._count?.participants ?? 0}</td>
                  <td className="p-3">{item._count?.decisions ?? 0}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="text-sm self-center">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      <ReviewFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleCreate}
      />
    </div>
  );
}
